/**
 * The exchange of the admin console's one-time code.
 *
 * `startHandoff()` spends the code `handoffCode.ts` kept from the address
 * (`POST /maker-api/auth/handoff`, once: it is single use) and stores the
 * session the way the wallet sign-in does, of kind `operator`: an AileFX
 * operator on AileFX's desk, ending at the answer's `expiresAt`. A refusal
 * keeps no session.
 *
 * The code is in the request body only. The call is never reported by the
 * API layer; an unexpected outcome is reported here by its status alone, and
 * the report's address no longer holds the fragment.
 */

import { api, ApiError } from "./api";
import { errorReporter } from "./errorReporter";
import { hasHandoffCode, takeHandoffCode } from "./handoffCode";
import { clearSession, saveSession } from "./session";

/** A malformed, unknown, used or expired code, and every other 401 or 400. */
export const HANDOFF_EXPIRED = "The link from the admin console has expired. Open the Maker portal again from the admin console.";
/** 429: the sign-in routes' budget for this browser is spent; the code is no good by the time it refills. */
export const HANDOFF_RATE_LIMITED = "Too many sign-in attempts from this browser. Wait a minute, then open the Maker portal again from the admin console.";
/** 503, no answer, anything unexpected: the code may be spent, so only a new link helps. */
export const HANDOFF_NO_ANSWER = "The Maker portal did not answer, so the link from the admin console could not be used. Open the Maker portal again from the admin console.";

export type HandoffOutcome = { kind: "signed-in" } | { kind: "refused"; text: string };

/** What a code can look like (32 bytes in base64url is 43 characters): anything else is refused without a call. */
const CODE_SHAPE = /^[A-Za-z0-9_-]{20,200}$/;

let pending: Promise<HandoffOutcome> | null = null;
let shown = false;

/**
 * The exchange of the code captured at load, started on the first call and
 * the same promise on every later one (React may call the gate's initializer
 * twice); null when the page was not opened with a code. Never rejects.
 */
export function startHandoff(): Promise<HandoffOutcome> | null {
  if (pending !== null) return pending;
  if (!hasHandoffCode()) return null;
  pending = exchange(takeHandoffCode() ?? "");
  return pending;
}

/**
 * The exchange the gate waits for, started if need be: kept, even settled,
 * until the gate has shown its outcome (it may settle while the descriptor
 * still loads), then null.
 */
export function pendingHandoff(): Promise<HandoffOutcome> | null {
  return shown ? null : startHandoff();
}

/** The gate showed the outcome: a gate mounted later starts from the stored session. */
export function markHandoffShown(): void {
  shown = true;
}

async function exchange(code: string): Promise<HandoffOutcome> {
  // This tab was opened for the desk: whatever session it held is not kept, signed in or refused.
  if (!CODE_SHAPE.test(code)) {
    clearSession();
    return { kind: "refused", text: HANDOFF_EXPIRED };
  }
  try {
    const res = await api.handoff(code);
    saveSession({ token: res.token, kind: "operator", operator: res.operator, company: res.company, expiresAt: res.expiresAt });
    return { kind: "signed-in" };
  } catch (err) {
    clearSession();
    return { kind: "refused", text: refusalText(err) };
  }
}

function refusalText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400 || err.status === 401) return HANDOFF_EXPIRED;
    if (err.status === 429) return HANDOFF_RATE_LIMITED;
    if (err.status === 503) return HANDOFF_NO_ANSWER;
  }
  // By its status (or the error's name) alone: never the request, which holds the code.
  errorReporter.capture("the handoff from the admin console failed", {
    level: "error",
    meta: { source: "handoff", ...(err instanceof ApiError ? { status: err.status ?? "no answer" } : { error: err instanceof Error ? err.name : typeof err }) },
  });
  return HANDOFF_NO_ANSWER;
}

/** Tests only: no exchange started. */
export function resetHandoff(): void {
  pending = null;
  shown = false;
}
